/*
 * How chat's outbox items leave the device (protocol.md §2.5.3): the socket frame each one turns
 * into. The outbox is core's and knows no wire; chat registers this for its two key prefixes — `c`
 * for a conversation or a thread, `e` for an edit or a delete — in its binding (`bind.ts`), where
 * the socket's request exists (`core/drafts/transport.ts`).
 */

import type { OutboxItem } from "../../core/drafts/index.ts"
import { conversationOf, parseKey, wireAttachmentRef } from "../../core/drafts/index.ts"

/** The key prefixes whose items this file turns into frames. */
export const CHAT_OUTBOX_PREFIXES = ["c", "e"] as const

/** The socket request one of chat's items turns into. */
export function chatOutboxRequest(item: OutboxItem): {
	type: string
	data: Record<string, unknown>
} {
	const p = item.payload
	switch (p.kind) {
		case "post": {
			const target = parseKey(item.target)
			const data: Record<string, unknown> = {
				conv: conversationOf(item.target),
				clientMsgId: item.clientMsgId,
				markdown: p.markdown,
				attachments: p.attachments.map(wireAttachmentRef),
			}
			if (target?.kind === "thread") {
				data.threadRootId = target.threadRootId
				// Only a reply can ask for the timeline as well; on anything else the server reads the
				// flag as nothing, and sending it would be a claim about a message that answers none.
				if (p.alsoToChannel === true) data.alsoToChannel = true
			}
			if (p.replyToId !== undefined) data.replyToId = p.replyToId
			return { type: "chat.message.send", data }
		}
		case "edit":
			return {
				type: "chat.message.edit",
				data: {
					id: p.messageId,
					clientMsgId: item.clientMsgId,
					markdown: p.markdown,
					attachments: p.attachments.map(wireAttachmentRef),
					baseVersion: p.baseVersion,
				},
			}
		case "delete":
			return {
				type: "chat.message.delete",
				data: { id: p.messageId, clientMsgId: item.clientMsgId },
			}
	}
}
